import { CheckCircle2, HelpCircle, Snowflake, XCircle } from "lucide-react";

import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";
import { minorUnitsFor } from "@/shared/utils/currencyMinorUnits";
import { isDecimalString } from "@/shared/utils/decimal";

import type { PayoutBatchDetail } from "../../types/payoutBatches.types";
import { balanceVerdict, remainingAfter } from "../../utils/batchSummary";
import { ProviderLiquidityRow } from "./ProviderLiquidityRow";

export interface BatchBalancePanelProps {
  batch: PayoutBatchDetail;
  /** The wallet's balance as the runtime pushes it; falls back to the summary's snapshot. */
  liveAvailable: string | null;
  liveWalletStatus: string | null;
  isInternal: boolean;
}

/**
 * The decision the review screen turns on: what the wallet holds, what the selection debits, what is left.
 * Staff also read the corridor's liquidity here, labelled by corridor and never by provider.
 */
export function BatchBalancePanel({
  batch,
  liveAvailable,
  liveWalletStatus,
  isInternal,
}: BatchBalancePanelProps) {
  const { t } = useTranslations("payoutBatches");
  const wallet = batch.summary.wallet;
  const currency = batch.source_currency;
  const digits = minorUnitsFor(currency);
  const available = liveAvailable ?? wallet?.available ?? null;
  const debit = batch.totals.debit_indicative_selected;
  const verdict = balanceVerdict(available, debit);
  const remaining = available && isDecimalString(available) ? remainingAfter(available, debit) : null;
  const frozen = (liveWalletStatus ?? wallet?.status ?? "active").toLowerCase() !== "active";

  return (
    <ColocaSurface padding="lg" className="flex h-full flex-col gap-5">
      <div className="space-y-1">
        <p className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
          {t("balance_title")} · {currency}
        </p>
        {available !== null ? (
          <ColocaAmountDisplay value={available} currency={currency} fractionDigits={digits} size="xl" />
        ) : (
          <p className="text-2xl font-semibold text-muted-foreground">—</p>
        )}
        <p className="text-xs text-muted-foreground">{t("balance_available")}</p>
      </div>

      <dl className="space-y-2 text-sm">
        <div className="flex items-baseline justify-between gap-4">
          <dt className="text-muted-foreground">{t("balance_debit")}</dt>
          <dd>
            <ColocaAmountDisplay value={debit} currency={currency} fractionDigits={digits} size="sm" />
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-4 border-t border-border pt-2">
          <dt className="text-muted-foreground">{t("balance_remaining")}</dt>
          <dd>
            {remaining !== null ? (
              <ColocaAmountDisplay
                value={remaining}
                currency={currency}
                fractionDigits={digits}
                size="sm"
                className={remaining.startsWith("-") ? "text-destructive" : undefined}
              />
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </dd>
        </div>
      </dl>

      <div className="mt-auto space-y-2">
        {frozen ? (
          <ColocaStatusPill variant="danger" icon={Snowflake}>
            {t("balance_frozen")}
          </ColocaStatusPill>
        ) : verdict === "sufficient" ? (
          <ColocaStatusPill variant="success" icon={CheckCircle2}>
            {t("balance_sufficient")}
          </ColocaStatusPill>
        ) : verdict === "insufficient" ? (
          <ColocaStatusPill variant="danger" icon={XCircle}>
            {t("balance_insufficient")}
          </ColocaStatusPill>
        ) : (
          <ColocaStatusPill variant="neutral" icon={HelpCircle}>
            {t("balance_unknown")}
          </ColocaStatusPill>
        )}
        {isInternal && (
          <ProviderLiquidityRow
            balance={batch.summary.provider_balance}
            country={batch.country}
            rail={batch.rail}
            receiveGross={batch.totals.receive_selected}
          />
        )}
      </div>
    </ColocaSurface>
  );
}
